import { i18n } from "@lingui/core"

import { FormValues } from "../../shared-components/main-information-step/types"
import { attributionTraits } from "../analytics"
import { isBrowser } from "../browser"
import {
  CAC_CALCULATOR,
  DEMO_CONTACT,
  DEMO_PROGRAM,
  GATED_CONTENT_DOWNLOAD_REQUEST,
  getHubspotCompanyUrl,
  INCENTIVE_CALCULATOR,
  LEGAL_DISBURSEMENTS,
  NEWSLETTER_SUBSCRIPTION,
  RECIPIENT_TOUR,
  TEST_REWARD,
} from "../constants"
import { isDefined } from "../types"
import { fetchPost } from "./tremendous"
import { ApiResponse, Data, HubspotResponse } from "./types"

const HUBSPOT_BLOCKED_EMAIL_ERROR_TYPE = "BLOCKED_EMAIL"
export const HUBSPOT_BLOCKED_EMAIL_ERROR_MESSAGE =
  "Enter a valid work email address (e.g. not gmail.com)"
export const getHubspotBlockedEmailLocalizedMessage = () =>
  i18n._("Enter a valid work email address (e.g. not gmail.com)")

export const formatFormValues = (
  values: Record<string, string | boolean | null>,
): Data[] =>
  Object.entries(values).reduce(
    (data: Data[], [name, value]) =>
      isDefined(value) && value !== null ? [...data, { name, value }] : data,
    [],
  )

const isBlocked = ({ errorType }: { errorType: string }) =>
  errorType === HUBSPOT_BLOCKED_EMAIL_ERROR_TYPE

const formatBody = (data: Data[]) => {
  const traits = attributionTraits()
  const pageUri = isBrowser() ? window.location.href : ""
  return {
    fields: [
      ...data,
      ...formatFormValues(traits),
      { name: "last_form_submission_page", value: pageUri },
    ],
    context: {
      hutk: traits?.hubspot_utk,
      pageUri,
      pageName: document.title,
    },
  }
}

const handleErrorResponse = (error: unknown): ApiResponse => ({
  errors: error instanceof Error ? error.message : "Something went wrong",
  success: false,
})

const request = async (data: Data[], path: string) => {
  try {
    const { status, errors, message } = await fetchPost<HubspotResponse>(
      path,
      formatBody(data),
    )
    if (errors?.some(isBlocked)) {
      return { errors: HUBSPOT_BLOCKED_EMAIL_ERROR_MESSAGE, success: false }
    }
    return { errors: message, success: !status }
  } catch (error) {
    return handleErrorResponse(error)
  }
}

const hubspotOwnerId = async (email: string) => {
  try {
    const domain = email.split("@")[1]
    const url = getHubspotCompanyUrl(domain)
    const { status, results, message } = await fetchPost<HubspotResponse>(url)
    return {
      success: !status,
      hubspotOwnerId: results?.[0]?.properties?.hubspot_owner_id,
      errors: message,
    }
  } catch (error) {
    return handleErrorResponse(error)
  }
}

export async function bookDemo(values: FormValues) {
  const isNameInEmail = [values.firstName, values.lastName].some(name =>
    values.email.split("@")[0].toLowerCase().includes(name.toLowerCase()),
  )
  const valuesWithHiddenField = { ...values, name_in_email: isNameInEmail }
  const contactResponse = await request(
    formatFormValues(valuesWithHiddenField),
    DEMO_CONTACT,
  )
  return contactResponse.success
    ? hubspotOwnerId(values.email)
    : contactResponse
}

export async function incentiveCalculatorHubspot(data: Data[]) {
  return request(data, INCENTIVE_CALCULATOR)
}

export async function cacCalculatorHubspot(data: Data[]) {
  return request(data, CAC_CALCULATOR)
}

export function bookDemoFinal(data: Data[]) {
  return request(data, DEMO_PROGRAM)
}

export function testReward(data: Data[]) {
  return request(data, TEST_REWARD)
}

export function recipientTourHubspot(data: Data[]) {
  return request(data, RECIPIENT_TOUR)
}

export function legalDisbursements(data: Data[]) {
  return request(data, LEGAL_DISBURSEMENTS)
}

export function newsletterSubscription(data: Data[]) {
  return request(data, NEWSLETTER_SUBSCRIPTION)
}

export function gatedContentDownloadRequest(data: Data[]) {
  return request(data, GATED_CONTENT_DOWNLOAD_REQUEST)
}
